// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AvailableTools } from './AvailableTools';
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, Eye, Loader2, Plug, X,
  ShieldCheck, Sparkles, Wrench, CalendarClock, Boxes, Workflow, Package,
} from 'lucide-react';

import { fetchConnections, type OnboardingConnectionRow } from '../services/onboardingApi';
import { resolveProvider } from '../components/onboarding/providerCatalog';
import {
  fetchScoutPlan, fetchSavedReport, runScout,
  type ScoutProposal, type ScoutReport, type ScoutResult, type ScoutSetting,
} from '../services/toolScoutApi';
import { createCapability } from '../services/capabilitiesApi';
import { aaiApi } from '../services/aaiApi';

/**
 * My Tools — connected apps + the Scout.
 *
 * Click a tool:
 *   - no saved report yet → a centered scan dialog (same dim backdrop as the
 *     Settings modal) shows
 *     the read-only probe steps live; when done it lands on the tool's details
 *     page with the fresh report.
 *   - report already stored → straight to the details page, zero tokens.
 * "Look again" on the details page reopens the scan dialog and overwrites the
 * stored report. Reports persist server-side per client+app.
 */

interface ToolCard {
  slug: string;
  label: string;
  color: string;
  initial: string;
  textColor?: string;
}

function activeConnections(connections: OnboardingConnectionRow[]): OnboardingConnectionRow[] {
  return (connections || []).filter((row) => {
    const status = String(row.status || '').toLowerCase();
    return status === 'connected' || status === 'active';
  });
}

function openOrbitWithPrompt(prompt: string, extra: Record<string, unknown> = {}) {
  window.dispatchEvent(new CustomEvent('orbit:open-with-prompt', {
    detail: { text: prompt, route: 'problem', context: 'problem', fresh: true, ...extra },
  }));
}

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const KIND_META: Record<string, { label: string; icon: React.ElementType; hint: string; cta: string }> = {
  skill: { label: 'Skill', icon: Wrench, hint: 'Runs when you ask', cta: 'Add to my skills' },
  routine: { label: 'Routine', icon: CalendarClock, hint: 'Recurring', cta: 'Add routine' },
  workflow: { label: 'Workflow', icon: Workflow, hint: 'Multi-step, configurable', cta: 'Set up workflow' },
  plugin: { label: 'Skill pack', icon: Package, hint: 'Related skills together', cta: 'Add the pack' },
  app_idea: { label: 'App to build', icon: Boxes, hint: 'Worth scoping properly', cta: 'Scope it with Orbit' },
};

function settingsToConfigBlock(settings: ScoutSetting[], values: Record<string, any>): string {
  if (!settings.length) return '';
  const lines = settings.map((s) => {
    const v = values[s.key];
    const rendered = Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v ?? '');
    return `- ${s.label}: ${rendered || '—'}`;
  });
  return `\n\n## Configuration (chosen by the user)\n${lines.join('\n')}`;
}

function friendlyScoutError(result: { error?: string; detail?: string }): string {
  if (result.error === 'synthesis_failed') {
    return 'I read the app fine, but stumbled while writing up the report. Try again — this usually works on the next pass.';
  }
  if (result.error === 'no_grounded_proposals') {
    return "I couldn't find anything concrete enough to suggest yet. Try again once there's more activity in this app.";
  }
  return result.detail || result.error || 'Something went wrong.';
}

// ── Setting field ────────────────────────────────────────────────────────────

const SettingField: React.FC<{
  setting: ScoutSetting;
  value: any;
  onChange: (v: any) => void;
}> = ({ setting, value, onChange }) => {
  const base = 'w-full rounded-lg border border-line bg-white px-3 py-2 text-[13px] text-ink outline-none focus:border-ink focus:ring-2 focus:ring-ink/15';
  return (
    <div>
      <label className="block text-[12.5px] font-medium text-ink">{setting.label}</label>
      {setting.hint && <p className="mb-1.5 mt-0.5 text-[11px] text-ink-soft">{setting.hint}</p>}
      {setting.type === 'select' && (
        <select className={base} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          {setting.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {setting.type === 'multi' && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {setting.options.map((o) => {
            const list: string[] = Array.isArray(value) ? value : [];
            const on = list.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => onChange(on ? list.filter((x) => x !== o) : [...list, o])}
                className={`rounded-full px-2.5 py-1 text-[11.5px] font-medium ring-1 transition-colors ${
                  on ? 'bg-brand-50 text-brand-700 ring-brand-200' : 'bg-white text-ink-dim ring-line hover:ring-brand-200'
                }`}
              >
                {on ? <Check className="mr-1 inline h-3 w-3" /> : null}{o}
              </button>
            );
          })}
        </div>
      )}
      {setting.type === 'time' && (
        <input type="time" className={base} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
      )}
      {setting.type === 'toggle' && (
        <button
          type="button"
          onClick={() => onChange(!value)}
          className={`mt-1 inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-brand-500' : 'bg-line'}`}
          aria-pressed={!!value}
        >
          <span className={`h-5 w-5 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      )}
      {setting.type === 'text' && (
        <input type="text" className={base} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
};

/*
 * F1. The schedule a routine never had.
 *
 * Accepting a "routine" suggestion used to create an ordinary skill with the
 * word routine attached, and nothing recurred, because no schedule existed
 * anywhere in the product. This asks for the one thing that makes it real.
 *
 * Three shapes and no cron. Whatever the person picks is read in THEIR zone,
 * which is why the browser's zone is sent with it: nine o'clock means nine
 * o'clock where they are.
 */
const SchedulePicker: React.FC<{
  cadence: string;
  time: string;
  weekday: number;
  onChange: (next: { cadence?: string; time?: string; weekday?: number }) => void;
}> = ({ cadence, time, weekday, onChange }) => (
  <div className="rounded-card border border-brand-100 bg-brand-50/50 p-3">
    <div className="mb-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-ink">
      <CalendarClock className="h-3.5 w-3.5 text-brand-600" /> When should it run?
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={cadence}
        onChange={(e) => onChange({ cadence: e.target.value })}
        className="rounded-lg border border-line px-2.5 py-1.5 text-[12.5px]"
      >
        <option value="daily">Every day</option>
        <option value="weekdays">Every weekday</option>
        <option value="weekly">Every week</option>
      </select>
      {cadence === 'weekly' && (
        <select
          value={weekday}
          onChange={(e) => onChange({ weekday: Number(e.target.value) })}
          className="rounded-lg border border-line px-2.5 py-1.5 text-[12.5px]"
        >
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
            .map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
      )}
      <input
        type="time"
        value={time}
        onChange={(e) => onChange({ time: e.target.value })}
        className="rounded-lg border border-line px-2.5 py-1.5 text-[12.5px]"
      />
    </div>
    <p className="mt-2 text-[11px] text-ink-dim">
      You can pause it or see what it produced in Settings, under Routines.
    </p>
  </div>
);

// ── Proposal detail dialog ───────────────────────────────────────────────────

const ProposalDialog: React.FC<{
  proposal: ScoutProposal;
  tool: ToolCard;
  clientId: string;
  onClose: () => void;
  onAccepted: (id: string) => void;
}> = ({ proposal, tool, clientId, onClose, onAccepted }) => {
  const meta = KIND_META[proposal.kind] || KIND_META.skill;
  const Icon = meta.icon;
  const [values, setValues] = useState<Record<string, any>>(() =>
    Object.fromEntries(proposal.settings.map((s) => [s.key, s.value])));
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');
  // F1: a routine needs a time. Defaults to every weekday at 9am, the shape a
  // morning briefing actually wants, so the common case is one click.
  const isRoutine = proposal.kind === 'routine';
  const [sched, setSched] = useState({ cadence: 'weekdays', time: '09:00', weekday: 0 });

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const accept = async () => {
    if (proposal.kind === 'app_idea') {
      openOrbitWithPrompt(
        `${proposal.scoping_prompt || proposal.title}\n\n(Proposed by the ${tool.label} scout — grounded in: ${proposal.evidence})`,
        { source: 'tool_scout', provider: tool.slug },
      );
      onClose();
      return;
    }
    setState('busy');
    try {
      if (proposal.kind === 'plugin' && proposal.skills?.length) {
        for (const s of proposal.skills) {
          await createCapability(clientId, {
            kind: 'skill',
            name: s.name,
            description: s.description,
            instructions: s.instructions,
            requiresApps: [tool.slug],
            category: proposal.title.slice(0, 60),
          });
        }
      } else if (proposal.skill_draft) {
        const instructions = proposal.skill_draft.instructions + settingsToConfigBlock(proposal.settings, values);
        await createCapability(clientId, {
          kind: 'skill',
          name: proposal.skill_draft.name,
          description: proposal.skill_draft.description,
          instructions,
          requiresApps: [tool.slug],
          category: proposal.kind === 'skill' ? 'scout' : proposal.kind,
        });
        // F1: a routine also needs a local skill and a schedule. The capability
        // above is what the builder's engine runs; this is what OUR scheduler
        // fires, as this person, through their own connections. Without it the
        // word routine would still be all a routine was.
        if (isRoutine) {
          const skill = await aaiApi.createMySkill({
            name: proposal.skill_draft.name,
            problem: proposal.skill_draft.description,
            process_steps: instructions.split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 20),
            kind: 'routine',
          });
          await aaiApi.createRoutine({
            skill_id: (skill.data as any).id,
            cadence: sched.cadence,
            time_of_day: sched.time,
            weekday: sched.cadence === 'weekly' ? sched.weekday : undefined,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          });
        }
      }
      setState('done');
      onAccepted(proposal.id);
      setTimeout(onClose, 900);
    } catch {
      setState('failed');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[130] !mt-0 flex items-center justify-center bg-black/40 p-4 sm:p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[min(640px,92vh)] w-[min(560px,100%)] flex-col overflow-hidden rounded-panel border border-line bg-white shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
        <div className="flex items-start gap-3 border-b border-line-soft p-5">
          <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-card ${proposal.kind === 'app_idea' ? 'bg-ink text-white' : 'bg-brand-50 text-brand-600 ring-1 ring-brand-100'}`}>
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ${proposal.kind === 'app_idea' ? 'bg-canvas-inset text-ink ring-line' : 'bg-brand-50 text-brand-700 ring-brand-100'}`}>
                {meta.label}
              </span>
              <span className="text-[11px] text-ink-soft">{meta.hint}</span>
            </div>
            <h3 className="mt-1 text-[17px] font-semibold leading-snug text-ink">{proposal.title}</h3>
            <p className="mt-0.5 text-[13px] text-ink-dim">{proposal.tagline}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-soft hover:bg-canvas-inset hover:text-ink"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          <div className="rounded-card bg-canvas px-3.5 py-2.5 text-[12.5px] leading-relaxed text-ink-muted ring-1 ring-gray-100">
            <span className="font-medium text-ink">Why I'm suggesting this: </span>{proposal.evidence}
          </div>

          {proposal.steps.length > 0 && (
            <div>
              <div className="mb-2 font-sans text-[10.5px] font-semibold uppercase tracking-[0.09em] text-brand-600">What it will do</div>
              <ol className="space-y-1.5">
                {proposal.steps.map((s, i) => (
                  <li key={s} className="flex items-start gap-2.5 text-[13px] text-ink">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-50 text-[10.5px] font-semibold text-brand-700 ring-1 ring-brand-100">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {proposal.kind === 'plugin' && proposal.skills && (
            <div>
              <div className="mb-2 font-sans text-[10.5px] font-semibold uppercase tracking-[0.09em] text-brand-600">In this pack</div>
              <div className="space-y-2">
                {proposal.skills.map((s) => (
                  <div key={s.name} className="rounded-panel border border-line bg-white px-3.5 py-2.5">
                    <div className="text-[13px] font-semibold text-ink">{s.name}</div>
                    <div className="text-[12px] text-ink-dim">{s.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isRoutine && (
            <SchedulePicker
              cadence={sched.cadence}
              time={sched.time}
              weekday={sched.weekday}
              onChange={(next) => setSched((cur) => ({ ...cur, ...next }))}
            />
          )}

          {proposal.settings.length > 0 && (
            <div>
              <div className="mb-2 font-sans text-[10.5px] font-semibold uppercase tracking-[0.09em] text-brand-600">Make it yours</div>
              <div className="space-y-3.5">
                {proposal.settings.map((s) => (
                  <SettingField key={s.key} setting={s} value={values[s.key]} onChange={(v) => setValues((m) => ({ ...m, [s.key]: v }))} />
                ))}
              </div>
            </div>
          )}

          {proposal.never_without_asking.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 rounded-card bg-green-50/60 px-3.5 py-2.5 ring-1 ring-green-100">
              <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
              <span className="text-[11.5px] font-medium text-green-800">Never without asking:</span>
              {proposal.never_without_asking.map((n) => (
                <span key={n} className="rounded-full bg-white px-2 py-0.5 text-[10.5px] text-ink-muted ring-1 ring-green-100">{n}</span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-line-soft p-4">
          <button type="button" onClick={onClose} className="rounded-card px-3 py-2 text-[12.5px] font-semibold text-ink-dim hover:text-ink">
            Not now
          </button>
          {state === 'done' ? (
            <span className="inline-flex items-center gap-1.5 rounded-card bg-green-50 px-4 py-2 text-[13px] font-semibold text-green-700 ring-1 ring-green-200">
              <CheckCircle2 className="h-4 w-4" /> Added — ask Orbit anytime
            </span>
          ) : (
            <button
              type="button"
              onClick={accept}
              disabled={state === 'busy'}
              className={`whitespace-nowrap inline-flex items-center gap-1.5 rounded-card px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-60 ${proposal.kind === 'app_idea' ? 'bg-ink hover:bg-ink' : 'bg-brand-500 hover:bg-brand-600'}`}
            >
              {state === 'busy' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {state === 'failed' ? 'Try again' : meta.cta}
              {proposal.kind === 'app_idea' && <ArrowRight className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Scan dialog (centered, light blur backdrop) ──────────────────────────────

interface ScanState {
  tool: ToolCard;
  steps: string[];
  stepDone: number;
  error: string | null;
  unsupported: boolean;
}

const ScanDialog: React.FC<{
  scan: ScanState;
  onRetry: () => void;
  onClose: () => void;
}> = ({ scan, onRetry, onClose }) => {
  const { tool, steps, stepDone, error, unsupported } = scan;
  const progress = steps.length ? Math.min(stepDone / steps.length, 1) : 0;
  const busy = !error && !unsupported;

  return (
    <div className="fixed inset-0 z-[130] !mt-0 flex items-center justify-center bg-black/40 p-4">
      <div className="w-[min(440px,100%)] overflow-hidden rounded-panel border border-line bg-white shadow-[0_24px_60px_rgba(15,23,42,0.2)]">
        <div className="flex items-center gap-3 border-b border-line-soft px-5 py-4">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-card text-[13px] font-bold"
            style={{ backgroundColor: tool.color, color: tool.textColor || '#fff' }}
          >
            {tool.initial}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[14.5px] font-semibold text-ink">
              {unsupported ? `Can't read ${tool.label} yet` : error ? `Couldn't finish reading ${tool.label}` : `Taking a look inside your ${tool.label}`}
            </div>
            <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-green-700">
              <ShieldCheck className="h-3 w-3" />
              Read-only — nothing is changed, sent, or shared.
            </div>
          </div>
          {!busy && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-soft hover:bg-canvas-inset hover:text-ink"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="px-5 py-5">
          {unsupported ? (
            <p className="text-[13px] leading-relaxed text-ink-dim">
              The scout currently knows how to read Gmail, Slack, and Asana. More apps are coming.
            </p>
          ) : error ? (
            <p className="text-[13px] leading-relaxed text-ink-dim">{error}</p>
          ) : (
            <>
              <div className="h-1 overflow-hidden rounded-full bg-canvas-inset">
                <div
                  className="h-full rounded-full bg-brand-500 transition-all duration-700"
                  style={{ width: `${Math.max(progress * 100, 6)}%` }}
                />
              </div>
              <div className="mt-4 space-y-2.5">
                {steps.length === 0 && (
                  <div className="flex items-center justify-center gap-2 py-2 text-sm text-ink-soft">
                    <Loader2 className="h-4 w-4 animate-spin" /> Preparing…
                  </div>
                )}
                {steps.map((label, i) => {
                  const done = i < stepDone;
                  const current = i === stepDone;
                  return (
                    <div key={label} className="flex items-center gap-2.5">
                      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full ${
                        done ? 'bg-green-100 text-green-700'
                          : current ? 'bg-brand-50 text-brand-600 ring-1 ring-brand-200'
                          : 'bg-canvas text-ink-soft ring-1 ring-gray-100'
                      }`}>
                        {done ? <Check className="h-3 w-3" /> : current ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="text-[9.5px]">{i + 1}</span>}
                      </span>
                      <span className={`text-[12.5px] ${done ? 'text-ink-soft' : current ? 'font-medium text-ink' : 'text-ink-soft'}`}>
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-4 text-center text-[11.5px] text-ink-soft">
                Takes about half a minute. When it's done you'll land on the report.
              </p>
            </>
          )}
        </div>

        {(error || unsupported) && (
          <div className="flex items-center justify-end gap-2 border-t border-line-soft px-5 py-3.5">
            <button type="button" onClick={onClose} className="rounded-card px-3 py-2 text-[12.5px] font-semibold text-ink-dim hover:text-ink">
              Close
            </button>
            {!unsupported && (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-card bg-brand-500 px-4 py-2 text-[12.5px] font-semibold text-white hover:bg-brand-600"
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Tool details page (the stored report) ────────────────────────────────────

const ToolDetails: React.FC<{
  clientId: string;
  tool: ToolCard;
  report: ScoutReport;
  onBack: () => void;
  onRescan: () => void;
}> = ({ clientId, tool, report, onBack, onRescan }) => {
  const [openProposal, setOpenProposal] = useState<ScoutProposal | null>(null);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());

  return (
    <div className="space-y-5 font-sans text-ink">
      <header className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-9 w-9 items-center justify-center rounded-card border border-line bg-white text-ink-dim shadow-sm hover:border-brand-200 hover:text-brand-700"
          aria-label="Back to tools"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span
          className="grid h-9 w-9 place-items-center rounded-card text-[13px] font-bold shadow-sm"
          style={{ backgroundColor: tool.color, color: tool.textColor || '#fff' }}
        >
          {tool.initial}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="text-[19px] font-semibold tracking-tight text-ink">Your {tool.label} report</h1>
          {report.generated_at && (
            <div className="mt-0.5 text-[11.5px] text-ink-soft">Scouted {timeAgo(report.generated_at)}</div>
          )}
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700 ring-1 ring-green-100">
          <ShieldCheck className="h-3.5 w-3.5" />
          Read-only
        </span>
        <button
          type="button"
          onClick={onRescan}
          className="inline-flex items-center gap-1.5 rounded-card border border-line bg-white px-3 py-2 text-[12.5px] font-semibold text-ink-muted shadow-sm hover:border-brand-200 hover:text-brand-700 whitespace-nowrap"
        >
          <Eye className="h-4 w-4" />
          Look again
        </button>
      </header>

      {report.headline && (
        <div className="rounded-panel border border-brand-100 bg-brand-50 px-5 py-4">
          <div className="flex items-start gap-2.5">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
            <p className="text-[14.5px] font-medium leading-snug text-ink">{report.headline}</p>
          </div>
        </div>
      )}

      {report.observations.length > 0 && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {report.observations.map((o) => (
            <div key={o.label} className="rounded-panel border border-brand-100 bg-brand-50 p-4">
              <div className="text-[24px] font-semibold tabular-nums tracking-tight text-ink">{o.stat}</div>
              <div className="mt-0.5 text-[12px] font-medium text-ink">{o.label}</div>
              {o.detail && <div className="mt-1 text-[11px] leading-snug text-ink-soft">{o.detail}</div>}
            </div>
          ))}
        </div>
      )}

      <section>
        <div className="mb-2.5 font-sans text-[11px] font-semibold uppercase tracking-[0.09em] text-brand-600">
          What I can take off your plate
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {report.proposals.map((p) => {
            const meta = KIND_META[p.kind] || KIND_META.skill;
            const Icon = meta.icon;
            const isBuild = p.kind === 'app_idea';
            const done = accepted.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setOpenProposal(p)}
                className="group flex flex-col rounded-panel border border-line bg-white p-4 text-left shadow-sm transition-all hover:border-brand-200 hover:shadow-md"
              >
                <div className="flex items-center gap-2.5">
                  <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-card ${isBuild ? 'bg-ink text-white' : 'bg-brand-50 text-brand-600 ring-1 ring-brand-100'}`}>
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ${isBuild ? 'bg-canvas-inset text-ink ring-line' : 'bg-brand-50 text-brand-700 ring-brand-100'}`}>
                    {meta.label}
                  </span>
                  {done && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-green-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Added
                    </span>
                  )}
                </div>
                <div className="mt-2.5 text-[14px] font-semibold leading-snug text-ink">{p.title}</div>
                <p className="mt-1 line-clamp-2 text-[12.5px] leading-snug text-ink-dim">{p.tagline}</p>
                <div className="mt-3 flex items-center justify-between border-t border-gray-50 pt-2.5">
                  <span className="text-[11px] text-ink-soft">{meta.hint}</span>
                  <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 opacity-0 transition-opacity group-hover:opacity-100">
                    View details <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {openProposal && (
        <ProposalDialog
          proposal={openProposal}
          tool={tool}
          clientId={clientId}
          onClose={() => setOpenProposal(null)}
          onAccepted={(id) => setAccepted((s) => new Set([...s, id]))}
        />
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const MyToolsPage: React.FC = () => {
  const clientId = localStorage.getItem('client_id') || '';
  const [params] = useSearchParams();
  const showCatalogue = Boolean(params.get('connect'));
  const [connections, setConnections] = useState<OnboardingConnectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [servers, setServers] = useState<any[]>([]);
  const [details, setDetails] = useState<{ tool: ToolCard; report: ScoutReport } | null>(null);
  const [scan, setScan] = useState<ScanState | null>(null);
  const [checkingSlug, setCheckingSlug] = useState<string | null>(null);
  const timerRef = useRef<any>(null);

  const loadConnections = useCallback(async () => {
    if (!clientId) { setConnections([]); setLoading(false); return; }
    setLoading(true);
    try {
      const rows = await fetchConnections(clientId);
      setConnections(rows || []);
    } catch (error) {
      console.warn('[my-tools] connections fetch failed', error);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadConnections(); }, [loadConnections]);
  useEffect(() => {
    aaiApi.getConnectors()
      .then(({ data }) => setServers((data.connectors || []).filter((c: any) => {
        return c.kind === 'mcp' && c.enabled && c.mcp?.connected;
      })))
      .catch(() => setServers([]));
  }, []);
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const tools = useMemo(() => {
    const seen = new Set<string>();
    const out: ToolCard[] = [];
    for (const row of activeConnections(connections)) {
      if (String(row.provider || '').toLowerCase().startsWith('mcp_')) continue;
      const { provider } = resolveProvider(row.provider);
      if (seen.has(provider.slug)) continue;
      seen.add(provider.slug);
      out.push(provider);
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [connections]);

  /** Run a fresh scout inside the scan dialog; land on details when done. */
  const startScan = useCallback(async (tool: ToolCard) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setScan({ tool, steps: [], stepDone: 0, error: null, unsupported: false });
    try {
      const plan = await fetchScoutPlan(clientId, tool.slug);
      if (!plan.supported) {
        setScan({ tool, steps: [], stepDone: 0, error: null, unsupported: true });
        return;
      }
      setScan({ tool, steps: plan.steps, stepDone: 0, error: null, unsupported: false });
      let i = 0;
      timerRef.current = setInterval(() => {
        i = Math.min(i + 1, Math.max(plan.steps.length - 1, 0));
        setScan((s) => (s && s.tool.slug === tool.slug ? { ...s, stepDone: i } : s));
      }, 2600);

      const result: ScoutResult = await runScout(clientId, tool.slug);
      clearInterval(timerRef.current);
      if (result.ok) {
        setScan((s) => (s && s.tool.slug === tool.slug ? { ...s, stepDone: plan.steps.length } : s));
        setTimeout(() => {
          setScan(null);
          setDetails({ tool, report: result });
        }, 450);
      } else {
        setScan({
          tool,
          steps: plan.steps,
          stepDone: 0,
          error: result.error === 'unsupported_provider' ? null : friendlyScoutError(result),
          unsupported: result.error === 'unsupported_provider',
        });
      }
    } catch (error: any) {
      if (timerRef.current) clearInterval(timerRef.current);
      setScan({ tool, steps: [], stepDone: 0, error: error?.message || 'Something went wrong.', unsupported: false });
    }
  }, [clientId]);

  /** Click a tool: saved report → straight to details; none → scan dialog. */
  const openTool = useCallback(async (tool: ToolCard) => {
    setCheckingSlug(tool.slug);
    try {
      const saved = await fetchSavedReport(clientId, tool.slug);
      if (saved) {
        setDetails({ tool, report: saved });
        return;
      }
    } catch { /* fall through to a fresh scan */ }
    finally {
      setCheckingSlug(null);
    }
    startScan(tool);
  }, [clientId, startScan]);

  if (details) {
    return (
      <>
        <ToolDetails
          clientId={clientId}
          tool={details.tool}
          report={details.report}
          onBack={() => setDetails(null)}
          onRescan={() => startScan(details.tool)}
        />
        {scan && (
          <ScanDialog
            scan={scan}
            onRetry={() => startScan(scan.tool)}
            onClose={() => setScan(null)}
          />
        )}
      </>
    );
  }

  return (
    <div className="space-y-6 font-sans text-ink">
      <header className="rounded-panel border border-line bg-white p-5 shadow-sm">
        <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.09em] text-brand-600">My tools</div>
        <div className="mt-1 flex items-center gap-3">
          <h1 className="text-[26px] font-semibold tracking-tight text-ink">Your connected apps.</h1>
          {!loading && (
            <span className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-700 ring-1 ring-brand-100">
              {tools.length} connected
            </span>
          )}
        </div>
        <p className="mt-2 max-w-[620px] text-[14px] leading-relaxed text-ink-dim">
          Pick an app and I'll take a read-only look inside, then tell you what I
          could take off your plate.
        </p>
      </header>

      {loading ? (
        <div className="flex items-center justify-center rounded-panel border border-line bg-white py-16 text-sm text-ink-dim shadow-sm">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading connected tools.
        </div>
      ) : tools.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tools.map((provider) => (
            <button
              key={provider.slug}
              type="button"
              onClick={() => openTool(provider)}
              disabled={checkingSlug === provider.slug}
              className="group flex items-center gap-3 rounded-panel border border-line bg-white p-4 text-left shadow-sm transition-all hover:border-brand-200 hover:shadow-md disabled:opacity-70"
            >
              <span
                className="grid h-10 w-10 shrink-0 place-items-center rounded-card text-[14px] font-bold"
                style={{ backgroundColor: provider.color, color: provider.textColor || '#ffffff' }}
              >
                {provider.initial}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold text-ink">{provider.label}</span>
                <span className="mt-0.5 flex items-center gap-1 text-[12px] text-ink-dim">
                  <Eye className="h-3.5 w-3.5 text-ink-soft transition-colors group-hover:text-brand-500" />
                  See what I can take off your plate
                </span>
              </span>
              {checkingSlug === provider.slug
                ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-500" />
                : <ArrowRight className="h-4 w-4 shrink-0 text-ink-soft transition-colors group-hover:text-brand-500" />}
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-panel border border-line bg-white px-5 py-16 text-center shadow-sm">
          <Plug className="mx-auto mb-3 h-8 w-8 text-ink-soft" />
          <div className="text-sm font-semibold text-ink">No tools connected yet.</div>
          <p className="mx-auto mt-1 max-w-[360px] text-sm text-ink-dim">
            Once you connect an app, it shows up here — and I can start proposing
            what to take off your plate.
          </p>
        </div>
      )}

      {servers.length > 0 && (
        <section className="space-y-3">
          <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-soft">MCP servers</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {servers.map((server) => (
              <div key={server.slug} className="flex items-center gap-3 rounded-panel border border-line bg-white p-4 shadow-sm">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-card bg-canvas-inset text-[14px] font-bold text-ink-muted">
                  {String(server.label || '?').charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="block truncate text-[14px] font-semibold text-ink">{server.label}</span>
                    <span className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[10.5px] font-semibold text-brand-700 ring-1 ring-brand-100">MCP</span>
                  </span>
                  <span className="mt-0.5 block text-[12px] text-ink-dim">Company server. Nothing to connect.</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {scan && (
        <ScanDialog
          scan={scan}
          onRetry={() => startScan(scan.tool)}
          onClose={() => setScan(null)}
        />
      )}
      {/* The catalogue, under the connections. A tool you have not connected
          did not exist on this screen, so finding one meant already knowing its
          name. Scoped by the server: the Owner sees all of them, everyone else
          sees what their department has been given. */}
      {showCatalogue && <AvailableTools />}

    </div>
  );
};

export default MyToolsPage;
